import type { ReactNode } from "react";
import { ratioReason, ratioValue } from "../api/client";
import type { Ratio } from "../api/types";

/** Renders one of loading / error / empty / content, in that priority order. */
export function StateBlock({
  loading,
  error,
  isEmpty,
  emptyMessage,
  onRetry,
  children,
}: {
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
  emptyMessage: string;
  onRetry?: () => void;
  children: ReactNode;
}) {
  if (loading) {
    return (
      <div className="state state--loading" role="status" aria-live="polite">
        <span className="spinner" aria-hidden="true" /> Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="state state--error" role="alert">
        <p>Something went wrong: {error}</p>
        {onRetry && (
          <button type="button" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }
  if (isEmpty) {
    return (
      <div className="state state--empty" role="status">
        {emptyMessage}
      </div>
    );
  }
  return <>{children}</>;
}

const currencyFmt = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const numberFmt = new Intl.NumberFormat();

export function money(value: number | undefined, currency = "USD"): string {
  if (value == null || !Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return currencyFmt.format(value);
  }
}

export function count(value: number | undefined): string {
  return value == null || !Number.isFinite(value)
    ? "—"
    : numberFmt.format(value);
}

/** Renders a Ratio: the numeric value, or its reason as muted text. */
export function RatioCell({
  ratio,
  suffix = "",
  digits = 2,
}: {
  ratio: Ratio | undefined | null;
  suffix?: string;
  digits?: number;
}) {
  const value = ratioValue(ratio);
  if (value != null) {
    return (
      <span>
        {value.toFixed(digits)}
        {suffix}
      </span>
    );
  }
  const reason = ratioReason(ratio);
  return (
    <span className="muted" title={reason ?? undefined}>
      n/a
    </span>
  );
}
